import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Card, CardContent, Button } from '@mui/material';
import { getArticles } from '../lib/supabase';
import type { Article } from '../types/database.types';

const WritingSection: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const data = await getArticles();
        // Get only the first 2 articles
        setArticles(data.slice(0, 2));
      } catch (err) {
        console.error('Error fetching articles:', err);
      }
    };

    fetchArticles();
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        {/* Section Title */}
        <Typography
          variant="h2"
          sx={{
            color: '#1a1a1a',
            mb: 4,
            textAlign: 'left',
            fontSize: { xs: '2.5rem', md: '3.5rem' },
          }}
        >
          MY WRITING
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: 'left',
            mb: 8,
            fontSize: { xs: '1rem', md: '1.125rem' },
          }}
        >
          I've been fortunate to contribute to some amazing publications. Check
          out my most recent pieces.
        </Typography>

        {/* Articles Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 4,
            mb: 6,
          }}
        >
          {articles.map((article) => (
            <Card
              key={article.id}
              sx={{
                height: '100%',
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
                transition: 'transform 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
              }}
              onClick={() => navigate(`/article/${article.slug}`)}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: '#999',
                    mb: 2,
                  }}
                >
                  {article.author} - {new Date(article.published_at || '').toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                    fontWeight: 900,
                    color: '#1a1a1a',
                    mb: 3,
                    fontFamily: '"Arial Black", sans-serif',
                  }}
                >
                  {article.title}
                </Typography>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/article/${article.slug}`);
                  }}
                  sx={{
                    color: '#dc143c',
                    fontWeight: 700,
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                    textDecorationThickness: '2px',
                    p: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                      color: '#b01030',
                    },
                  }}
                >
                  Read Full Article
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* See All Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            onClick={() => navigate('/articles')}
            sx={{
              color: '#1a1a1a',
              fontWeight: 700,
              fontSize: '1.125rem',
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
              textDecorationThickness: '2px',
              p: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
                color: '#dc143c',
              },
            }}
          >
            See all Publications
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default WritingSection;